// "New purchases" on Overview: what Hush logged by itself and has not been looked at. Section one,
// "Needs a decision": purchases with no category (a new merchant's suggestion pre-selected),
// alerts Hush could not read, and guesses it filed from a suggestion ("Check these guesses").
// Section two, ask mode only, "Filed for you": purchases Hush filed itself in the last 7 days,
// with the reason. The card goes away when it is empty; no badge, no push.
import type { OverviewDto, TransactionDto } from '../../../shared/api';
import { filedByHush, filedReason } from '../../../shared/filed';
import { esc, money } from '../format';
import { cardName } from './activity';
import { reviewRow } from './review';
import type { NewSplit } from './splits';
import type { NewTip } from './tips';
import { canFund, funds } from './savings';
import { fundAgain } from '../../../shared/savings';
import { TIP_PERCENTS, tipFor } from '../../../shared/tips';
import { cleanNote, splitNoteOf } from '../../../shared/notes';
import { ALL_THEIRS_PERCENT, SPLIT_DEFAULT_PERCENT } from '../../../shared/splits';
import { noteLine } from './notes';

/** "Filed for you" lists purchases from this many days. */
export const FILED_FOR_YOU_DAYS = 7;
/** Rows shown in "Filed for you" before "See all". */
export const FILED_FOR_YOU_ROWS = 3;

const dayBefore = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);

/** Spending purchases (never money set aside or a Done entry). */
const spendingTx = (d: OverviewDto) => {
  const setAside = new Set(d.categories.filter((k) => k.kind === 'set_aside').map((k) => k.id));
  return d.transactions.filter((t) => t.source !== 'transfer' && !(t.categoryId && setAside.has(t.categoryId)));
};

/**
 * Delete on a New purchases row and in its Edit sheet: a quiet text button, so it is never taken
 * for Confirm. No confirm step: it deletes at once (main.ts new-del, Activity's delete path) and the
 * toast has Undo.
 */
export const deleteButton = (id: string, from = '') =>
  `<button class="newdel" data-a="new-del" data-id="${esc(id)}"${from ? ` data-from="${esc(from)}"` : ''}>Delete</button>`;

/** The card's rows, newest first. `today` is the overview's day. */
export function newPurchases(d: OverviewDto, today: string) {
  const all = spendingTx(d).sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt));
  const unseen = all.filter((t) => !t.reviewed);
  // A purchase with no category needs a decision however it arrived (one added by hand too).
  const decide = all.filter((t) => !t.categoryId);
  const guesses = unseen.filter((t) => t.categoryId && t.filedBy === 'suggested');
  const since = dayBefore(today, FILED_FOR_YOU_DAYS - 1);
  const filed = d.user.mode === 'ask' ? unseen.filter((t) => t.categoryId && t.filedBy !== 'suggested' && filedByHush(t) && t.date >= since) : [];
  return { decide, guesses, filed, reviewItems: d.reviewItems };
}

/** The reason for one purchase, in plain words. */
export const reasonFor = (d: Pick<OverviewDto, 'categories'>, t: TransactionDto) =>
  filedReason({ filedBy: t.filedBy, filedDetail: t.filedDetail, category: d.categories.find((k) => k.id === t.categoryId)?.name ?? null, merchant: t.merchant });

const nameOf = (d: OverviewDto, id: string | null) => d.categories.find((k) => k.id === id)?.name ?? '';

/** What the user chose on a row (or in its Edit sheet) before Confirm. Absent: as Hush filed it. */
export interface RowChoices {
  pick?: string;
  tip?: NewTip;
  split?: NewSplit;
  /** The fund picked ('' none). */
  fund?: string;
  /** The Edit sheet's "Paid from savings" switch (absent: on when the purchase already has a fund). */
  fundOn?: boolean;
  /** The item picked (null: none). */
  item?: string | null;
  /** "What was this for?" as typed in the Edit sheet (absent: the purchase's own note). */
  note?: string;
  /** The amount typed in the Edit sheet of an auto-logged charge, in cents (null: not a valid amount). */
  amount?: number | null;
}

/** Every row's choices, kept in main.ts ui (one map each). */
export interface NewChoices {
  picks: Map<string, string>;
  splits: Map<string, NewSplit>;
  tips: Map<string, NewTip>;
  funds: Map<string, string>;
  items: Map<string, string | null>;
  fundOn: Map<string, boolean>;
  notes?: Map<string, string>;
  amounts?: Map<string, number | null>;
}

export const choicesOf = (c: Partial<NewChoices>, id: string): RowChoices => ({
  pick: c.picks?.get(id),
  tip: c.tips?.get(id),
  split: c.splits?.get(id),
  fund: c.funds?.get(id),
  item: c.items?.has(id) ? c.items.get(id) : undefined,
  fundOn: c.fundOn?.get(id),
  note: c.notes?.get(id),
  amount: c.amounts?.has(id) ? c.amounts.get(id) : undefined,
});

/**
 * An auto-logged charge (an item or a same-amount bill logged its planned amount) can have its
 * amount corrected in its Edit sheet. Alerts keep the bank's amount here (the edit sheet still
 * changes it).
 */
export const canEditAmount = (t: TransactionDto) => t.source === 'recurring';

/** The bank charge a row shows and confirms: a valid amount typed in its Edit sheet, else its own. */
export const rowCharge = (t: TransactionDto, c: RowChoices) => (c.amount != null && c.amount > 0 ? c.amount : t.amountCents);

/** The amount a row's Confirm saves: {} unchanged, false when what was typed is not an amount. */
export function rowAmountPatch(t: TransactionDto, c: RowChoices): { amountCents?: number } | false {
  if (c.amount === undefined || !canEditAmount(t)) return {};
  if (c.amount === null || !(c.amount > 0)) return false;
  return c.amount === t.amountCents ? {} : { amountCents: c.amount };
}
export const AMOUNT_NEEDED = 'Enter an amount above $0.';

/** A split I sent that is still open (waiting, or accepted and not removed): a new amount changes it. */
export const openSentSplit = (t: TransactionDto) =>
  !!t.split && t.split.direction === 'sent' && !t.split.endedReason && (t.split.status === 'pending' || t.split.status === 'accepted');

/** Confirm all never sends a split: a new amount on a split purchase is confirmed on its own row. */
export const AMOUNT_SPLIT_ALONE = (merchant: string) => `Confirm ${merchant} on its own: its new amount changes a split.`;

/** The note a row saves and shows: typed in its Edit sheet, else the purchase's own. */
export const rowNote = (t: TransactionDto, c: RowChoices) => (c.note !== undefined ? cleanNote(c.note) : t.note);

/** The split as its switch turns on: 50/50. Its note is the row's "What was this for?" at Confirm. */
export const startSplit = (): NewSplit => ({ pct: SPLIT_DEFAULT_PERCENT });

/**
 * The note a row's split sends: its "What was this for?" as the Edit sheet shows it (rowNote()),
 * trimmed and cut to fit ('' none). Shown there with "Tiffany will see this." while the split is on.
 */
export const rowSplitNote = (t: TransactionDto, c: RowChoices) => splitNoteOf(rowNote(t, c));

/** The Tip switch: on while a tip is being set (absent: on when the purchase already has one). */
export const tipOn = (t: TransactionDto, c: RowChoices) => (c.tip ? !c.tip.off : !!t.tipCents);

/** The "Paid from savings" switch: absent, on when a fund is picked (or the purchase already has one). */
export const fundOn = (t: TransactionDto, c: RowChoices) => c.fundOn ?? (c.fund !== undefined ? !!c.fund : !!t.fundedFromCategoryId);

/** The fund picked while the switch is on ('' none yet). */
export const fundPicked = (t: TransactionDto, c: RowChoices) => (fundOn(t, c) ? (c.fund ?? t.fundedFromCategoryId ?? '') : '');

/** "Paid from savings" is on with no fund chosen: Confirm waits ("Choose a fund"). */
export const fundMissing = (t: TransactionDto, c: RowChoices) => fundOn(t, c) && !fundPicked(t, c);
export const CHOOSE_FUND = 'Choose a fund';

/** The category a row files under: the user's pick, else what Hush filed (or suggests). */
export const rowCategory = (t: TransactionDto, c: RowChoices) => c.pick ?? t.categoryId ?? t.suggestedCategoryId ?? null;

/**
 * One muted line naming what is set on a row, or '' when nothing is: "Split 50/50, tip 18%, paid
 * from Medical". A tip that is one of the quick picks reads as its percent, any other in dollars.
 */
export function rowSummary(d: OverviewDto, t: TransactionDto, c: RowChoices): string {
  const parts: string[] = [];
  const amount = rowAmountPatch(t, c);
  if (amount && amount.amountCents) parts.push(`amount ${money(amount.amountCents, 2)}`);
  if (c.split) parts.push(c.split.pct === 50 ? 'split 50/50' : c.split.pct === ALL_THEIRS_PERCENT ? `all ${d.partner.linked?.name ?? 'your partner'}'s` : `split ${c.split.pct}% me`);
  // What the partner will read, shown on the row too (Confirm and split can be tapped from here).
  // (The row's own "What was this for?": the split sends it.)
  const note = c.split ? rowSplitNote(t, c) : '';
  if (note) parts.push(`note "${note}"`);
  if (c.tip?.tipCents) {
    const pct = TIP_PERCENTS.find((p) => tipFor(rowCharge(t, c), p) === c.tip!.tipCents);
    parts.push(pct ? `tip ${pct}%` : `tip ${money(c.tip.tipCents, 2)}`);
  }
  const fund = c.fund && c.fundOn !== false ? d.categories.find((k) => k.id === c.fund)?.name : null;
  if (fund) parts.push(`paid from ${fund}`);
  const item = c.item ? d.items.find((i) => i.id === c.item)?.name : null;
  if (item) parts.push(`for ${item}`);
  const line = parts.join(', ');
  return line ? line[0].toUpperCase() + line.slice(1) : '';
}

/** "Confirm", or "Confirm and split" while the row's split is on. */
export const confirmLabel = (c: RowChoices) => (c.split ? 'Confirm and split' : 'Confirm');

export const NO_CATEGORY_LINE = 'No category yet. Pick one in Edit.';
export const SPLITS_NOT_SENT = 'Splits are not sent';

/** The category line: its name, then why (the reason Hush filed it, or that it is the user's pick). */
function categoryLine(d: OverviewDto, t: TransactionDto, c: RowChoices): string {
  const cat = rowCategory(t, c);
  if (!cat) return `<p class="hint">${esc(NO_CATEGORY_LINE)}</p>`;
  const why = c.pick && c.pick !== t.categoryId ? 'Your pick.' : t.categoryId ? reasonFor(d, t) : 'Suggested for this merchant.';
  return `<p class="hint"><b>${esc(nameOf(d, cat))}</b>. ${esc(why)}</p>`;
}

/**
 * One New purchases row, collapsed: merchant, amount, category and why, a summary of anything set,
 * "Paid from <fund> again?" when the merchant has a usual fund (one tap, never picked by itself),
 * then Confirm and Edit. `action` is new-confirm (needs a category) or new-ok (already filed).
 */
function newRow(d: OverviewDto, t: TransactionDto, c: RowChoices, action: 'new-confirm' | 'new-ok'): string {
  const id = esc(t.id);
  const card = t.cardLast4 ? cardName(d, t.cardLast4) : null;
  const summary = rowSummary(d, t, c);
  const usual = t.usualFundId && !fundOn(t, c) && canFund(d, t) ? funds(d).find((k) => k.id === t.usualFundId) : undefined;
  const again = usual ? `<button class="linkbtn fundagain" data-a="new-fund-pick" data-id="${id}" data-fund="${esc(usual.id)}">${esc(fundAgain(usual.name))}</button>` : '';
  const missing = fundMissing(t, c);
  const ready = !!rowCategory(t, c) && !missing;
  return `<div class="askrow newrow" data-tx="${id}"><div class="askhead"><span>${esc(t.merchant)}${card ? ` <small class="muted">${esc(card)}</small>` : ''}</span><span>${money(t.countedCents, 2)}</span></div>
  ${noteLine(rowNote(t, c))}${categoryLine(d, t, c)}${summary ? `<p class="hint newsum">${esc(summary)}</p>` : ''}${missing ? `<p class="hint newsum">${CHOOSE_FUND} in Edit.</p>` : ''}${again}
  <div class="acts"><button class="btn primary" data-a="${action}" data-id="${id}"${ready ? '' : ' disabled'}>${confirmLabel(c)}</button><button class="btn" data-a="new-edit" data-id="${id}">Edit</button>${deleteButton(t.id)}</div></div>`;
}

/**
 * "Confirm all N" at a section's header (two rows or more). It never splits, tips or marks anything
 * paid from savings; while a row in the section has its split on it says so under the button.
 */
function confirmAll(rows: TransactionDto[], ids: string[], c: Partial<NewChoices>, action: 'new-confirm-all' | 'new-ok-all'): string {
  if (ids.length < 2) return '';
  const splitOn = rows.some((t) => c.splits?.has(t.id));
  return `<div class="newall"><button class="btn" data-a="${action}" data-ids="${esc(ids.join(','))}">Confirm all ${ids.length}</button>${splitOn ? `<p class="hint">${SPLITS_NOT_SENT}</p>` : ''}</div>`;
}

const section = (title: string, all: string, body: string) => `<div class="newhead"><h3>${title}</h3>${all}</div>${body}`;

/**
 * The card, or '' when there is nothing new. Quiet mode shows only what needs a decision and the
 * guesses to check; ask mode adds "Filed for you" (3 rows, "See all"). Rows are collapsed: tip,
 * split, paid from savings and item are set in the row's Edit sheet (views/newsheet.ts) and shown
 * as one summary line. "Confirm all" never splits, tips or marks anything paid from savings.
 */
export function vNewPurchases(
  d: OverviewDto,
  today: string,
  picks: Map<string, string> = new Map(),
  splits: Map<string, NewSplit> = new Map(),
  tips: Map<string, NewTip> = new Map(),
  funds: Map<string, string> = new Map(),
  items: Map<string, string | null> = new Map(),
  fundOnMap: Map<string, boolean> = new Map(),
  notes: Map<string, string> = new Map(),
  amounts: Map<string, number | null> = new Map(),
): string {
  // Every month: its own purchases (an old month stays fully editable).
  const c: NewChoices = { picks, splits, tips, funds, items, fundOn: fundOnMap, notes, amounts };
  const n = newPurchases(d, today);
  const parts: string[] = [];
  if (n.decide.length || n.reviewItems.length) {
    // Only rows with a category (picked or suggested) can be confirmed in one go.
    const ready = n.decide.filter((t) => rowCategory(t, choicesOf(c, t.id)));
    parts.push(
      section(
        'Needs a decision',
        confirmAll(n.decide, ready.map((t) => t.id), c, 'new-confirm-all'),
        `${n.reviewItems.map(reviewRow).join('')}${n.decide.map((t) => newRow(d, t, choicesOf(c, t.id), 'new-confirm')).join('')}`,
      ),
    );
  }
  if (n.guesses.length)
    parts.push(section('Check these guesses', confirmAll(n.guesses, n.guesses.map((t) => t.id), c, 'new-ok-all'), n.guesses.map((t) => newRow(d, t, choicesOf(c, t.id), 'new-ok')).join('')));
  if (n.filed.length) {
    const shown = n.filed.slice(0, FILED_FOR_YOU_ROWS);
    parts.push(
      section(
        'Filed for you',
        confirmAll(n.filed, n.filed.map((t) => t.id), c, 'new-ok-all'),
        `${shown.map((t) => newRow(d, t, choicesOf(c, t.id), 'new-ok')).join('')}${n.filed.length > FILED_FOR_YOU_ROWS ? '<div class="acts"><button class="linkbtn" data-a="see-new">See all</button></div>' : ''}`,
      ),
    );
  }
  return parts.length ? `<section class="ask newcard"><h2>New purchases</h2>${parts.join('')}</section>` : '';
}
